import type { LineStatus, LocalText, Order, OrderLine } from './types';

export type Lane = 'new' | 'cooking' | 'ready';
const ACTIVE: LineStatus[] = ['queued', 'cooking', 'ready'];

/** The earliest stage any of the ticket's (visible) dishes is still in: what its "all" button moves next. */
export function stageOf(o: Order, visible: (l: OrderLine) => boolean = () => true): Lane | null {
  const st = o.lines.filter(visible).map((l) => l.status);
  if (st.includes('queued')) return 'new';
  if (st.includes('cooking')) return 'cooking';
  if (st.includes('ready')) return 'ready';
  return null;
}

/**
 * Where a ticket sits on the table view: "new" until the kitchen touches it, "ready" once every unfinished dish
 * is ready, otherwise "cooking" (work has started on at least one dish).
 */
export function laneOf(o: Order, visible: (l: OrderLine) => boolean = () => true): Lane | null {
  const st = o.lines.filter(visible).map((l) => l.status).filter((x) => ACTIVE.includes(x));
  if (!st.length) return null;
  if (st.every((x) => x === 'queued')) return 'new';
  if (st.every((x) => x === 'ready')) return 'ready';
  return 'cooking';
}

/** One tap on a dish moves it one step. */
export const NEXT_STEP: Partial<Record<LineStatus, LineStatus>> = { queued: 'cooking', cooking: 'ready', ready: 'served' };

/** The ticket's "all" button moves every visible dish that is behind to the next stage. */
export function advance(o: Order, visible: (l: OrderLine) => boolean = () => true): { status: LineStatus; lineIds: string[] } | null {
  const lane = stageOf(o, visible);
  if (!lane) return null;
  const from: Record<Lane, LineStatus[]> = { new: ['queued'], cooking: ['queued', 'cooking'], ready: ['ready'] };
  const to: Record<Lane, LineStatus> = { new: 'cooking', cooking: 'ready', ready: 'served' };
  return { status: to[lane], lineIds: o.lines.filter((l) => visible(l) && from[lane].includes(l.status)).map((l) => l.id) };
}

export interface DishRef { sessionId: string; orderId: string; lineId: string; qty: number; table: string; status: LineStatus; placedAt: string }
export interface DishGroup { key: string; name: LocalText; choices: LocalText[]; note?: string; queued: DishRef[]; cooking: DishRef[]; total: number; oldest: string }

/**
 * "By dish" view: identical dishes (same item, same options, same note) across all tables, so the kitchen can
 * cook five กะเพรา at once. Ready dishes are left to the "to serve" list.
 */
export function groupByDish(orders: Order[], visible: (l: OrderLine) => boolean = () => true): DishGroup[] {
  const groups = new Map<string, DishGroup>();
  for (const o of orders) {
    for (const l of o.lines) {
      if (!visible(l) || (l.status !== 'queued' && l.status !== 'cooking')) continue;
      const key = `${l.itemId}|${l.choices.map((c) => c.choiceId).sort().join('+')}|${l.note ?? ''}`;
      const g = groups.get(key) ?? { key, name: l.name, choices: l.choices.map((c) => c.name), note: l.note, queued: [], cooking: [], total: 0, oldest: o.placedAt };
      const ref = { sessionId: o.sessionId, orderId: o.id, lineId: l.id, qty: l.qty, table: o.tableName, status: l.status, placedAt: o.placedAt };
      (l.status === 'queued' ? g.queued : g.cooking).push(ref);
      g.total += l.qty;
      if (o.placedAt < g.oldest) g.oldest = o.placedAt;
      groups.set(key, g);
    }
  }
  return [...groups.values()].sort((a, b) => a.oldest.localeCompare(b.oldest));
}

export const isActive = (o: Order) => o.lines.some((l) => ACTIVE.includes(l.status));
export const minutesSince = (iso: string, now: number) => Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
export const LATE_MINUTES = 15;
